import { CurrentModuleContext } from 'src/components/GHSAuthoringAssistant/GHSAuthoringAssistant'
import { APP_CONFIG } from 'src/config.app'
import { ModuleDTO } from 'src/models/dto/ModuleDTO'
import { ADAxios, ApiActionNames } from 'src/services/AxiosInterceptor'
import { handleAxiosError } from 'src/services/backend/ModuleService'

export interface ChatMessage {
    content: string
    sender: 'user' | 'assistant'
    timestamp: Date
}

export interface MessageResponseContent {
    answer: string
}

export interface AnalysisResponseContent {
    batch_retrieved_context: string
    context_analysis: string
}

export class ChatClient {
    private readonly chat_endpoint = `${APP_CONFIG.authoringAssistantAPIBaseUrl}/chat`
    private readonly analysis_endpoint = `${APP_CONFIG.authoringAssistantAPIBaseUrl}/analysis`

    async getAnalysis(
        currentModuleContext?: CurrentModuleContext,
        currentModuleDTO?: ModuleDTO,
        abortController?: AbortController
    ): Promise<AnalysisResponseContent> {
        try {
            const payload = {
                currentModuleContext: currentModuleContext
                    ? JSON.stringify(currentModuleContext)
                    : undefined,
                currentModuleDTO: currentModuleDTO ? JSON.stringify(currentModuleDTO) : undefined,
            }

            const { data } = await ADAxios.post(this.analysis_endpoint, payload, {
                apiActionName: ApiActionNames.GHSAuthoringAssistant,
                signal: abortController?.signal,
                timeout: 60000,
            })

            return data as unknown as AnalysisResponseContent
        } catch (error) {
            console.error('Error generating analysis from GHS Authoring Assistant:', error)
            handleAxiosError(error)
        }
    }

    async sendMessage(
        message: string,
        currentModuleContext?: CurrentModuleContext,
        currentModuleDTO?: ModuleDTO,
        contextAnalysis?: string,
        batchRetrievedContext?: string,
        abortController?: AbortController
    ): Promise<ChatMessage> {
        try {
            const payload = {
                question: message,
                currentModuleContext: currentModuleContext
                    ? JSON.stringify(currentModuleContext)
                    : undefined,
                currentModuleDTO: currentModuleDTO ? JSON.stringify(currentModuleDTO) : undefined,
                contextAnalysis: contextAnalysis,
                batchRetrievedContext: batchRetrievedContext,
            }

            const { data } = await ADAxios.post(this.chat_endpoint, payload, {
                apiActionName: ApiActionNames.GHSAuthoringAssistant,
                signal: abortController?.signal,
                timeout: 60000,
            })

            const answer = (data as unknown as MessageResponseContent).answer

            return {
                content: answer,
                sender: 'assistant',
                timestamp: new Date(),
            }
        } catch (error) {
            console.error('Error sending message to GHS Authoring Assistant:', error)
            handleAxiosError(error)

            return {
                content:
                    'Sorry, I encountered an error while processing your request. Please try again later.',
                sender: 'assistant',
                timestamp: new Date(),
            }
        }
    }
}

function initializeChatClient(): ChatClient {
    return new ChatClient()
}

export const chatClient: ChatClient = initializeChatClient()
